// City-to-city route pages (NYC to ...). Prices are the point-to-point quote from Times Square,
// shown as "from NYC" (see prices.json; the distance used is in each route's distanceMi). A route
// with no quote in prices.json gets no page.
import { BOOK_POINT, EXTRAS, INCLUDED } from './common';
import { cheapest, driveTime, money, route } from './prices';

const AREA = ['New York City', 'Manhattan', 'Brooklyn', 'Queens'];

function cityPage(key, page) {
  const r = route(key);
  if (!r) return null;
  const from = { sedan: r.sedan, suv: r.suv };
  return {
    book: BOOK_POINT,
    from,
    fromPrice: cheapest(from),
    fromNote: 'from NYC',
    included: INCLUDED.point,
    extras: EXTRAS.point,
    priceTitle: <>NYC to {page.city} <em>prices</em></>,
    priceLead: `Your private car from New York City to ${page.city}, by vehicle. The price is per vehicle, not per passenger.`,
    priceTables: [{
      caption: `Car service prices from NYC to ${page.city}`,
      firstColumn: 'Trip',
      rows: [{ key, label: `NYC → ${r.place}`, sedan: r.sedan, suv: r.suv }],
    }],
    priceNote: `Prices are for a pickup in Midtown Manhattan and a drop-off in ${r.place} (about ${r.distanceMi} miles). Other addresses are priced by distance in the same way.`,
    facts: [
      ['Distance', `about ${r.distanceMi} miles`],
      ['Drive time', `${driveTime(r.durationMinutes)} without heavy traffic`],
      ['Sedan', money(r.sedan)],
      ['SUV', money(r.suv)],
    ],
    schema: {
      name: `Car service from NYC to ${page.city}`,
      serviceType: 'Long-distance black car service',
      areaServed: [...AREA, page.city],
    },
    ...page,
    route: r,
  };
}

// One page for several New Jersey suburbs: a price row per town, all from Times Square.
const NJ_TOWNS = [
  ['montclair-nj', 'NYC → Montclair'],
  ['ridgewood-nj', 'NYC → Ridgewood'],
  ['summit-nj', 'NYC → Summit'],
  ['chatham-nj', 'NYC → Chatham'],
];

function njSuburbsPage() {
  const towns = NJ_TOWNS.map(([key, label]) => ({ key, label, r: route(key) })).filter((t) => t.r);
  if (towns.length === 0) return null;
  const from = { sedan: Math.min(...towns.map((t) => t.r.sedan)), suv: Math.min(...towns.map((t) => t.r.suv)) };
  const price = (key) => route(key) && `${money(route(key).sedan)} in a Sedan and ${money(route(key).suv)} in an SUV`;
  const names = towns.map((t) => t.r.place.replace(', NJ', ''));
  return {
    path: '/nyc-to-new-jersey-car-service',
    city: 'New Jersey',
    linkText: 'NYC to Montclair, Ridgewood, Summit, Chatham',
    eyebrow: 'Car service · NYC to New Jersey',
    title: <>NYC to Montclair, Ridgewood, <em>Summit and Chatham</em></>,
    lead: 'A private chauffeur between Manhattan and the New Jersey suburbs. Home after a late night in the city, to the office in the morning, or to the airport.',
    heroImage: 'route-ridgewood-dusk',
    book: BOOK_POINT,
    cta: 'Book your New Jersey ride',
    from,
    fromPrice: cheapest(from),
    fromNote: 'from NYC',
    included: INCLUDED.point,
    extras: EXTRAS.point,
    introEyebrow: 'Black car service to New Jersey',
    introTitle: <>Across the Hudson, <em>to your door</em></>,
    intro: [
      `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} are some of the New Jersey towns our chauffeurs drive to most. All of them are between about 19 and 26 miles from Midtown Manhattan, and without heavy traffic each one is roughly 35 to 40 minutes away.`,
      'Montclair, in Essex County, is reached through the Lincoln Tunnel and Route 3. Ridgewood, in Bergen County, is usually reached over the George Washington Bridge or through the Lincoln Tunnel and up Route 17. Summit and Chatham, further south-west, are reached through the Holland or Lincoln Tunnel and then I-78 and Route 24.',
      'People book these rides to get home after dinner, a show or a late flight without waiting for the last train, to start the workday in Manhattan with time to take calls, or to get the whole family into the city for an event. Bridge and tunnel tolls are included in the price.',
    ],
    facts: [
      ['Distance', 'about 19–26 miles'],
      ['Drive time', 'about 35–40 min without heavy traffic'],
      ['Tolls', 'Included'],
      ['From', money(cheapest(from))],
    ],
    priceTitle: <>NYC to New Jersey <em>prices</em></>,
    priceLead: 'Your private car from Midtown Manhattan to each town, by vehicle. The price is per vehicle, not per passenger.',
    priceTables: [{
      caption: 'Car service prices from NYC to New Jersey towns',
      firstColumn: 'Trip',
      rows: towns.map((t) => ({ key: t.key, label: t.label, sedan: t.r.sedan, suv: t.r.suv })),
    }],
    priceNote: 'Prices are for a pickup in Midtown Manhattan and a drop-off in the centre of each town. Other addresses are priced by distance in the same way.',
    sections: [
      {
        eyebrow: 'Timing and traffic',
        title: <>The tunnels <em>and the bridge</em></>,
        paragraphs: [
          'Most of the time on these trips is spent getting out of Manhattan. Heading to New Jersey, the Lincoln and Holland Tunnels are busiest on weekday afternoons from about 3:30 to 7 pm; heading into the city, from about 6:30 to 9:30 am. At those times the drive can take 60 to 90 minutes.',
          'Late evenings and weekend mornings are usually quick. Your price is fixed once you book, so traffic costs you time, not money.',
        ],
        image: 'skyline-light-trails',
        imageAlt: 'Manhattan skyline with light trails from traffic at night',
      },
      {
        eyebrow: 'Popular for',
        title: <>Home, office, <em>airport</em></>,
        paragraphs: ['We drive to any address in these towns and nearby. Rides to Newark Airport from Summit and Chatham are short; see our Newark page for airport prices.'],
        list: ['Late nights home from Manhattan', 'Morning rides to the office', 'Family trips into the city for events', 'Rides to JFK, LaGuardia and Newark'],
      },
    ],
    faqTitle: 'NYC to New Jersey questions',
    faq: [
      route('montclair-nj') && { q: 'How much is a car service from NYC to Montclair?', a: `From Midtown Manhattan to Montclair, ${price('montclair-nj')}. This is an estimate; your exact price is shown before you pay.` },
      route('ridgewood-nj') && { q: 'How much is a car service from NYC to Ridgewood?', a: `From Midtown Manhattan to Ridgewood, ${price('ridgewood-nj')}. This is an estimate; your exact price is shown before you pay.` },
      route('summit-nj') && route('chatham-nj') && { q: 'How much is a car service from NYC to Summit or Chatham?', a: `To Summit, ${price('summit-nj')}; to Chatham, ${price('chatham-nj')}. These are estimates; your exact price is shown before you pay.` },
      { q: 'Are bridge and tunnel tolls included?', a: 'Yes. Lincoln Tunnel, Holland Tunnel and George Washington Bridge tolls are included in the price, like all tolls.' },
      { q: 'How long does the drive take?', a: 'About 35 to 40 minutes to each town without heavy traffic, and 60 to 90 minutes at weekday rush hour.' },
      { q: 'Can you pick me up in New Jersey and take me into the city?', a: 'Yes. Enter your New Jersey address as the pickup and your Manhattan address as the drop-off; the price is calculated the same way.' },
      { q: 'Do you go to other New Jersey towns?', a: 'Yes, anywhere in New Jersey. Every ride is priced by distance and the price is shown before you book.' },
    ].filter(Boolean),
    related: ['/newark-airport-car-service', '/point-to-point-car-service', '/hourly-chauffeur-service', '/nyc-to-philadelphia-car-service'],
    schema: {
      name: 'Car service from NYC to New Jersey suburbs',
      serviceType: 'Black car service',
      areaServed: [...AREA, ...towns.map((t) => t.r.place)],
    },
  };
}

const PAGES = [
  cityPage('hamptons', {
    path: '/nyc-to-the-hamptons-car-service',
    city: 'the Hamptons',
    linkText: 'NYC to the Hamptons',
    eyebrow: 'Car service · NYC to the Hamptons',
    title: <>NYC to the Hamptons, <em>door to door</em></>,
    lead: 'A private chauffeur from your door in the city to your door in Southampton, East Hampton or Montauk. Luggage, dogs in carriers and beach bags welcome.',
    heroImage: 'route-hamptons-beach',
    cta: 'Book your Hamptons ride',
    introEyebrow: 'Black car service to the Hamptons',
    introTitle: <>Your weekend <em>starts at home</em></>,
    intro: [
      'The drive from Manhattan to the Hamptons runs east on the Long Island Expressway, then along Sunrise Highway and Montauk Highway into the South Fork. Southampton is roughly 90 miles from Midtown. With clear roads it takes about two hours; on a summer Friday afternoon it can take twice that.',
      'A private car service turns that time into something you can use. Instead of changing at Jamaica for the train or waiting at a bus stop with your bags, your chauffeur picks you up at home, loads your luggage, and drops you at the house. You can work, call ahead, or sleep the whole way.',
      'Most Hamptons rides are weekend trips between Memorial Day and Labor Day, but we also drive guests to weddings in Bridgehampton and Water Mill, families to rentals for the week, and homeowners out for a weekend in the off season, when the roads are clear.',
    ],
    sections: [
      {
        eyebrow: 'Timing and traffic',
        title: <>When to <em>leave the city</em></>,
        paragraphs: [
          'In summer, the worst traffic is Friday from about 1 pm to 8 pm heading east, and Sunday from early afternoon into the evening heading back. Leaving before noon on Friday, or after 8 pm, usually saves an hour or more. Holiday weekends start a day early.',
          'Once you have booked, your price does not change with traffic, so a slow Friday costs you time, not money. Further east than Southampton, East Hampton, Amagansett and Montauk are longer drives and are priced by the extra distance.',
        ],
        image: 'skyline-light-trails',
        imageAlt: 'Traffic light trails leaving Manhattan at night',
      },
      {
        eyebrow: 'Sedan or SUV',
        title: <>Room for <em>a weekend</em></>,
        paragraphs: [
          'A Sedan suits up to 3 passengers with 3 large bags. For a family, a group sharing a house, or anyone bringing golf clubs, coolers or a stroller, the SUV seats up to 6 with room for 6 large bags.',
          'Pets are welcome in a closed carrier, and service animals are always welcome, as set out in our Terms.',
        ],
        list: ['Southampton, Bridgehampton, Water Mill', 'East Hampton, Sag Harbor, Amagansett', 'Montauk and Westhampton', 'Return rides to the city'],
      },
    ],
    faqTitle: 'NYC to the Hamptons questions',
    faq: [
      { q: 'Are tolls included?', a: 'Yes. Bridge, tunnel and turnpike tolls are included in the price. Only an optional tip, and the charges in our Terms such as extra waiting time, can be added.' },
      { q: 'How much is a car service from NYC to the Hamptons?', a: `A Sedan from Midtown Manhattan to Southampton is ${money(route('hamptons').sedan)} and an SUV is ${money(route('hamptons').suv)}. Further east, the price is calculated by the extra distance and shown before you book.` },
      { q: 'How long does the drive take?', a: 'About 2 hours to Southampton without heavy traffic. On summer Friday afternoons and Sunday evenings it can take 3 to 4 hours or more.' },
      { q: 'Does the price go up in summer traffic?', a: 'Not after you book: your price is set when you confirm and does not change during the ride. The price shown on this page is an estimate; the exact price for your addresses and time is shown before you pay.' },
      { q: 'Can I bring my dog?', a: 'Yes, in a closed carrier. Service animals are always welcome. Please mention any pets in the notes when you book.' },
      { q: 'Can you pick us up in the Hamptons for the ride back?', a: 'Yes. Book a second point-to-point ride with your Hamptons address as the pickup and your city address as the drop-off.' },
      { q: 'Can we stop on the way?', a: 'Yes. Add a stop when you book and the price updates before you pay. For several stops, an hourly booking may suit you better.' },
    ],
    related: ['/hourly-chauffeur-service', '/nyc-to-greenwich-ct-car-service', '/jfk-car-service', '/point-to-point-car-service'],
  }),

  cityPage('philadelphia', {
    path: '/nyc-to-philadelphia-car-service',
    city: 'Philadelphia',
    linkText: 'NYC to Philadelphia',
    eyebrow: 'Car service · NYC to Philadelphia',
    title: <>NYC to Philadelphia, <em>in about two hours</em></>,
    lead: 'A private chauffeur from your New York address to Center City, University City or the Navy Yard. No station transfers, no waiting for a train.',
    heroImage: 'route-philadelphia-skyline',
    cta: 'Book your Philadelphia ride',
    introEyebrow: 'Black car service to Philadelphia',
    introTitle: <>Meetings, games, <em>family</em></>,
    intro: [
      'Philadelphia is about 95 miles from Midtown Manhattan. The usual route crosses into New Jersey through the Lincoln or Holland Tunnel and follows the New Jersey Turnpike south before crossing the Delaware into Pennsylvania. Without heavy traffic, the drive takes a little under two hours.',
      'The train is fast between stations, but a private car goes from your door to theirs. For a meeting in Center City, a visit to Penn or Drexel, a Phillies or Eagles game, or a family occasion in the suburbs, your chauffeur takes you straight there and you arrive without changing at 30th Street.',
      'Business travellers often use the time in the car to prepare, take calls in private and arrive ready. Groups of up to 6 can travel together in an SUV for one price per vehicle.',
    ],
    sections: [
      {
        eyebrow: 'Timing and traffic',
        title: <>When to <em>set off</em></>,
        paragraphs: [
          'Leaving Manhattan between about 7 and 10 am, or between 3 and 7 pm on weekdays, adds time at the tunnels and on the Turnpike. For a morning meeting in Philadelphia, a 6 am pickup usually gets you there comfortably. On Friday afternoons and before holidays, expect the drive to take longer.',
          'Your price is fixed when you book, so traffic changes your arrival time, not your fare. If you need to arrive by a set time, tell us in the notes and plan your pickup with room to spare.',
        ],
        image: 'manhattan-street-night',
        imageAlt: 'Manhattan street at night before a long-distance ride',
      },
      {
        eyebrow: 'Where we go',
        title: <>Across <em>Philadelphia</em></>,
        paragraphs: [
          'We drive to any address in Philadelphia and its suburbs. Prices on this page are for Center City; other destinations are priced by distance and shown before you book.',
        ],
        list: ['Center City and Rittenhouse', 'University City (Penn and Drexel)', 'South Philadelphia stadiums', 'Philadelphia International Airport (PHL)'],
      },
    ],
    faqTitle: 'NYC to Philadelphia questions',
    faq: [
      { q: 'Are tolls included?', a: 'Yes. Bridge, tunnel and turnpike tolls are included in the price. Only an optional tip, and the charges in our Terms such as extra waiting time, can be added.' },
      { q: 'How much is a car service from NYC to Philadelphia?', a: `From Midtown Manhattan to Center City, a Sedan is ${money(route('philadelphia').sedan)} and an SUV is ${money(route('philadelphia').suv)}. The exact price for your addresses is shown before you book.` },
      { q: 'How long is the drive?', a: 'A little under 2 hours without heavy traffic. Weekday rush hours and Friday afternoons can add 30 to 60 minutes.' },
      { q: 'Is a car service better than the train?', a: 'It depends on your trip. A private car is door to door, carries your luggage, and one price covers everyone in the vehicle, which helps for groups, early starts and trips that don’t begin or end near a station.' },
      { q: 'Can you drive me to Philadelphia airport?', a: 'Yes. Enter PHL as your drop-off. The ride is priced by distance and the total is shown before you pay.' },
      { q: 'Can I book a return trip?', a: 'Yes. Book a second ride from Philadelphia to New York. If you will spend only a few hours there and want the same chauffeur to wait, an hourly booking may suit you better.' },
    ],
    related: ['/nyc-to-washington-dc-car-service', '/nyc-to-atlantic-city-car-service', '/newark-airport-car-service', '/hourly-chauffeur-service'],
  }),

  cityPage('boston', {
    path: '/nyc-to-boston-car-service',
    city: 'Boston',
    linkText: 'NYC to Boston',
    eyebrow: 'Car service · NYC to Boston',
    title: <>NYC to Boston, <em>door to door</em></>,
    lead: 'A private chauffeur from New York to downtown Boston, Cambridge or Logan Airport. Work, rest or take calls in private the whole way.',
    heroImage: 'route-boston-skyline',
    cta: 'Book your Boston ride',
    introEyebrow: 'Black car service to Boston',
    introTitle: <>Hours on the road, <em>yours to use</em></>,
    intro: [
      'Boston is about 210 miles from Midtown Manhattan, usually via I-95 through Connecticut and Rhode Island, or inland via I-84 and the Massachusetts Turnpike. Without heavy traffic the drive takes about three and three-quarter hours.',
      'A private car makes sense when the trip starts or ends away from a station, when you are travelling with a lot of luggage, or when several people are going together. It is also a good alternative when trains are sold out or a short flight would mean two airport transfers and security lines.',
      'Our chauffeurs drive this route for business trips, university move-ins and visits to Cambridge, medical appointments at Boston hospitals, and families travelling with children who would rather not change trains.',
    ],
    sections: [
      {
        eyebrow: 'Timing and traffic',
        title: <>Planning <em>the drive</em></>,
        paragraphs: [
          'The slowest stretches are usually leaving New York through the Bronx and lower Westchester, and arriving in Boston at rush hour. A mid-morning start, around 9:30 or 10 am, often avoids both. Friday afternoons and holiday weekends are the busiest times on I-95.',
          'If you need a short break on a drive this long, tell us in the notes when you book. A planned stop can be added to your route, and the price updates before you pay.',
        ],
        image: 'night-ride-rear-seat',
        imageAlt: 'Rear seat of a private car on a long-distance ride',
      },
      {
        eyebrow: 'Where we go',
        title: <>Across <em>greater Boston</em></>,
        paragraphs: ['We drive to any address in Boston and the surrounding area. Prices on this page are for downtown Boston; other addresses are priced by distance before you book.'],
        list: ['Downtown, Back Bay and the Seaport', 'Cambridge (Harvard and MIT)', 'Boston Logan Airport (BOS)', 'Providence on the way'],
      },
    ],
    faqTitle: 'NYC to Boston questions',
    faq: [
      { q: 'Are tolls included?', a: 'Yes. Bridge, tunnel and turnpike tolls are included in the price. Only an optional tip, and the charges in our Terms such as extra waiting time, can be added.' },
      { q: 'How much is a car service from NYC to Boston?', a: `From Midtown Manhattan to downtown Boston, a Sedan is ${money(route('boston').sedan)} and an SUV is ${money(route('boston').suv)}. The price is per vehicle, so it covers everyone travelling together.` },
      { q: 'How long does the drive take?', a: 'About 3 hours 45 minutes without heavy traffic. Rush hours at either end, Friday afternoons and holiday weekends can add an hour or more.' },
      { q: 'Can we stop during the trip?', a: 'Yes. Add a stop when you book, for example in Connecticut or Providence. The price updates to include it before you pay.' },
      { q: 'Can you take me to Boston Logan Airport?', a: 'Yes. Enter Logan Airport as your drop-off; the price is calculated by distance and shown before you book.' },
      { q: 'How far in advance should I book?', a: 'For a long-distance ride, book at least a day ahead, and earlier for holiday weekends. You can change or cancel free of charge up to 1 hour before pickup.' },
    ],
    related: ['/nyc-to-new-haven-car-service', '/nyc-to-greenwich-ct-car-service', '/nyc-to-philadelphia-car-service', '/laguardia-car-service'],
  }),

  cityPage('washington-dc', {
    path: '/nyc-to-washington-dc-car-service',
    city: 'Washington, DC',
    linkText: 'NYC to Washington, DC',
    eyebrow: 'Car service · NYC to Washington, DC',
    title: <>NYC to Washington, DC, <em>in private</em></>,
    lead: 'A chauffeur from your New York address to Capitol Hill, downtown DC or Northern Virginia. A quiet, private space to work on the way.',
    heroImage: 'route-washington-capitol',
    cta: 'Book your DC ride',
    introEyebrow: 'Black car service to Washington',
    introTitle: <>Arrive <em>ready to work</em></>,
    intro: [
      'Washington, DC is about 225 miles from Midtown Manhattan, down the New Jersey Turnpike and I-95 through Delaware and around Baltimore. Without heavy traffic the drive takes about four hours.',
      'Clients book this ride when they need privacy for calls on the way, when their schedule doesn’t fit a train, when they are carrying documents or equipment, or when a team of up to 6 needs to arrive together. It is door to door, from your office or apartment in New York to the address of your meeting in DC.',
      'We also drive families to visits and college tours, guests to events at the Kennedy Center and on the Mall, and travellers to Reagan National or Dulles when they would rather not fly from New York.',
    ],
    sections: [
      {
        eyebrow: 'Timing and traffic',
        title: <>Beat the <em>Beltway</em></>,
        paragraphs: [
          'The busiest parts of the trip are leaving New York in the morning and evening rush, the Baltimore area, and arriving on the Capital Beltway between about 3:30 and 7 pm. For a morning meeting in DC, many clients leave New York at 5 or 6 am, or travel the evening before.',
          'Your price is set when you book and does not change if traffic is heavy. Add your schedule to the booking notes and we will plan the pickup with you.',
        ],
        image: 'skyline-light-trails',
        imageAlt: 'City lights and traffic at night',
      },
      {
        eyebrow: 'Where we go',
        title: <>DC, Maryland <em>and Virginia</em></>,
        paragraphs: ['Prices on this page are for downtown Washington. Capitol Hill, Georgetown, Arlington, Alexandria and Bethesda are priced by distance and shown before you book.'],
        list: ['Capitol Hill and downtown DC', 'Georgetown and Embassy Row', 'Arlington and Alexandria, VA', 'Reagan National (DCA) and Dulles (IAD)'],
      },
    ],
    faqTitle: 'NYC to Washington, DC questions',
    faq: [
      { q: 'Are tolls included?', a: 'Yes. Bridge, tunnel and turnpike tolls are included in the price. Only an optional tip, and the charges in our Terms such as extra waiting time, can be added.' },
      { q: 'How much is a car service from NYC to Washington, DC?', a: `From Midtown Manhattan to downtown Washington, a Sedan is ${money(route('washington-dc').sedan)} and an SUV is ${money(route('washington-dc').suv)}, per vehicle.` },
      { q: 'How long is the drive?', a: 'About 4 hours without heavy traffic. Rush hour around Baltimore and on the Beltway can add an hour or more.' },
      { q: 'Can I work during the ride?', a: 'Yes. Every car has Wi-Fi and a phone charger, and the rear cabin is private, so you can take calls and work on the way.' },
      { q: 'Can a group travel together?', a: 'Yes. The SUV seats up to 6 passengers with 6 large bags, for one price per vehicle.' },
      { q: 'Can you drop me at a DC airport?', a: 'Yes. Enter Reagan National or Dulles as your drop-off. The price is calculated by distance and shown before you pay.' },
    ],
    related: ['/nyc-to-philadelphia-car-service', '/nyc-to-boston-car-service', '/hourly-chauffeur-service', '/point-to-point-car-service'],
  }),

  cityPage('atlantic-city', {
    path: '/nyc-to-atlantic-city-car-service',
    city: 'Atlantic City',
    linkText: 'NYC to Atlantic City',
    eyebrow: 'Car service · NYC to Atlantic City',
    title: <>NYC to Atlantic City, <em>arrive in style</em></>,
    lead: 'A private chauffeur from New York to your Atlantic City hotel or casino. Celebrations, concerts and conventions, with room for the whole group.',
    heroImage: 'route-atlantic-city-boardwalk',
    cta: 'Book your Atlantic City ride',
    introEyebrow: 'Black car and limo service to Atlantic City',
    introTitle: <>The boardwalk, <em>from your door</em></>,
    intro: [
      'Atlantic City is about 130 miles from Midtown Manhattan, down the New Jersey Turnpike and the Garden State Parkway to the shore. Without heavy traffic the drive takes about two hours.',
      'Most people go for a night or a weekend: a birthday, a bachelor or bachelorette party, a concert or a fight at one of the casino venues, or a convention at the Atlantic City Convention Center. A private car takes the group straight to the hotel entrance with all the bags, and nobody has to drive home after a late night.',
      'An SUV seats up to 6 for a single price per vehicle. Many groups book a ride down on Friday or Saturday and a second ride back on Sunday.',
    ],
    sections: [
      {
        eyebrow: 'Timing and traffic',
        title: <>Shore <em>traffic</em></>,
        paragraphs: [
          'In summer, the Garden State Parkway is busiest heading south on Friday afternoons and Saturday mornings, and heading north on Sunday afternoons. Leaving the city before noon on a Friday or travelling on a weekday is usually much quicker. Around major concerts and fights, allow extra time near the casinos.',
          'Your price is fixed when you book, so a slow drive doesn’t change what you pay.',
        ],
        image: 'night-ride-rear-seat',
        imageAlt: 'Rear seat of a private car at night',
      },
      {
        eyebrow: 'Popular for',
        title: <>Nights out <em>and events</em></>,
        paragraphs: ['We drive to every hotel and casino in Atlantic City and to other addresses along the Jersey Shore. Prices on this page are for Atlantic City; other shore towns are priced by distance.'],
        list: ['Birthdays and bachelor or bachelorette weekends', 'Concerts, fights and shows', 'Conventions and trade shows', 'Beach weekends at the Jersey Shore'],
      },
    ],
    faqTitle: 'NYC to Atlantic City questions',
    faq: [
      { q: 'Are tolls included?', a: 'Yes. Bridge, tunnel and turnpike tolls are included in the price. Only an optional tip, and the charges in our Terms such as extra waiting time, can be added.' },
      { q: 'How much is a car service from NYC to Atlantic City?', a: `From Midtown Manhattan to Atlantic City, a Sedan is ${money(route('atlantic-city').sedan)} and an SUV is ${money(route('atlantic-city').suv)}. The price is per vehicle, not per person.` },
      { q: 'How long does it take to drive to Atlantic City?', a: 'About 2 hours without heavy traffic. Summer weekends and big event nights can add an hour or more.' },
      { q: 'How many people fit in the car?', a: 'Up to 3 passengers in a Sedan and up to 6 in an SUV. The vehicle can’t carry more passengers than it has seats.' },
      { q: 'Can you pick us up from the casino for the ride home?', a: 'Yes. Book a second ride with your hotel or casino as the pickup. Free waiting is 15 minutes after the pickup time, so plan your time to leave.' },
      { q: 'Can we make a stop on the way down?', a: 'Yes. Add the stop when you book and the price updates to include it before you pay.' },
    ],
    related: ['/nyc-to-philadelphia-car-service', '/hourly-chauffeur-service', '/newark-airport-car-service', '/point-to-point-car-service'],
  }),

  cityPage('greenwich-ct', {
    path: '/nyc-to-greenwich-ct-car-service',
    city: 'Greenwich, CT',
    linkText: 'NYC to Greenwich, CT',
    eyebrow: 'Car service · NYC to Greenwich, CT',
    title: <>NYC to Greenwich, <em>in under an hour</em></>,
    lead: 'A private chauffeur between Manhattan and Greenwich, Old Greenwich, Cos Cob or Riverside. For the office, the weekend or the airport.',
    heroImage: 'route-greenwich-waterfront',
    cta: 'Book your Greenwich ride',
    introEyebrow: 'Black car service to Connecticut',
    introTitle: <>The quick trip <em>north</em></>,
    intro: [
      'Greenwich is about 34 miles from Midtown Manhattan, on I-95 or the Hutchinson River and Merritt Parkways. Without heavy traffic the drive takes 50 to 60 minutes; at rush hour it can take 90.',
      'It is one of the most common rides our chauffeurs make: finance professionals between offices in Midtown and Greenwich, families heading home after a night in the city, guests going to weddings and events at clubs and estates, and residents going to and from JFK, LaGuardia and Newark.',
      'Compared with Metro-North, a private car saves the walk to Grand Central and the ride from the station at the other end, which makes a real difference late at night or with luggage.',
    ],
    sections: [
      {
        eyebrow: 'Timing and traffic',
        title: <>Reverse <em>commute</em></>,
        paragraphs: [
          'Weekday mornings are busiest heading into Manhattan and evenings heading out, but the reverse commute is not empty: I-95 through the Bronx and New Rochelle is slow most weekday afternoons. The Merritt Parkway is often quicker for cars, and your chauffeur chooses the best route on the day.',
          'Your price is fixed when you book. For a day of meetings in Connecticut, or a dinner where you want the car to wait, an hourly booking may be better value.',
        ],
        image: 'chauffeur-hands-wheel',
        imageAlt: 'A chauffeur’s hands on the steering wheel',
      },
      {
        eyebrow: 'Where we go',
        title: <>Greenwich <em>and nearby</em></>,
        paragraphs: ['Prices on this page are for central Greenwich. Neighbouring towns are priced by distance and shown before you book.'],
        list: ['Greenwich Avenue and downtown', 'Old Greenwich, Riverside and Cos Cob', 'Stamford and Darien', 'Westchester County Airport (HPN)'],
      },
    ],
    faqTitle: 'NYC to Greenwich questions',
    faq: [
      { q: 'Are tolls included?', a: 'Yes. Bridge, tunnel and turnpike tolls are included in the price. Only an optional tip, and the charges in our Terms such as extra waiting time, can be added.' },
      { q: 'How much is a car service from NYC to Greenwich?', a: `From Midtown Manhattan to Greenwich, a Sedan is ${money(route('greenwich-ct').sedan)} and an SUV is ${money(route('greenwich-ct').suv)}, per vehicle.` },
      { q: 'How long does the drive take?', a: 'About 50 to 60 minutes without heavy traffic, and up to 90 minutes at rush hour.' },
      { q: 'Can you take me from Greenwich to JFK, LaGuardia or Newark?', a: 'Yes. Enter your Greenwich address as the pickup and the airport as the drop-off. Add your flight number and the price is shown before you book.' },
      { q: 'Can you set up a regular commute?', a: 'Yes. We can set up daily or weekly rides on a regular route, and companies can open a corporate account with several bookers and one monthly invoice. Call or email us to arrange it.' },
      { q: 'Can the chauffeur wait while I’m at a meeting?', a: 'Free waiting is 15 minutes after pickup. If you want the car to wait longer, book an hourly chauffeur instead; waiting between stops is then included.' },
    ],
    related: ['/nyc-to-stamford-ct-car-service', '/nyc-to-new-haven-car-service', '/nyc-to-the-hamptons-car-service', '/laguardia-car-service'],
  }),
  cityPage('stamford-ct', {
    path: '/nyc-to-stamford-ct-car-service',
    city: 'Stamford, CT',
    linkText: 'NYC to Stamford, CT',
    eyebrow: 'Car service · NYC to Stamford, CT',
    title: <>NYC to Stamford, <em>office to office</em></>,
    lead: 'A private chauffeur between Manhattan and Stamford, for the commute, client meetings at Harbor Point or the ride home after a late night in the city.',
    heroImage: 'route-stamford-shore',
    cta: 'Book your Stamford ride',
    introEyebrow: 'Black car service to Stamford',
    introTitle: <>Connecticut’s <em>business city</em></>,
    intro: [
      'Stamford is about 40 miles from Midtown Manhattan, on I-95 or the Hutchinson River and Merritt Parkways. Without heavy traffic the drive takes about an hour; at weekday rush hour it can take 90 minutes or more.',
      'With its corporate headquarters, trading floors and the offices around Harbor Point and downtown, Stamford is one of the busiest business destinations outside New York City. Many of our clients ride between a Manhattan office and a Stamford one in the same day, working on calls in the back seat instead of changing trains at Grand Central.',
      'We also drive families home to Stamford after dinner or a show in the city, students to UConn Stamford, and travellers between Stamford and JFK, LaGuardia, Newark or Westchester County Airport.',
    ],
    sections: [
      {
        eyebrow: 'Timing and traffic',
        title: <>Beating <em>the I-95 crawl</em></>,
        paragraphs: [
          'I-95 through the Bronx, New Rochelle and Port Chester is the slow part of the trip, especially from about 7 to 10 am and 3:30 to 7 pm on weekdays. The Merritt Parkway is often the quicker route for cars, and your chauffeur picks the best one on the day.',
          'For a 9 am meeting in Stamford, a pickup around 7:15 in Manhattan usually gets you there with time to spare. Your price is fixed once you book, so a slow drive costs you time, not money.',
        ],
        image: 'chauffeur-hands-wheel',
        imageAlt: 'A chauffeur’s hands on the steering wheel during a drive',
      },
      {
        eyebrow: 'Where we go',
        title: <>Across <em>Stamford</em></>,
        paragraphs: ['Prices on this page are for central Stamford. Other addresses in Stamford and nearby towns are priced by distance and shown before you book.'],
        list: ['Downtown Stamford and Harbor Point', 'Stamford Transportation Center', 'Shippan, Old Greenwich and Darien', 'Westchester County Airport (HPN)'],
      },
    ],
    faqTitle: 'NYC to Stamford questions',
    faq: [
      { q: 'How much is a car service from NYC to Stamford?', a: `From Midtown Manhattan to central Stamford, a Sedan is ${money(route('stamford-ct').sedan)} and an SUV is ${money(route('stamford-ct').suv)}, per vehicle. This is an estimate; the exact price for your addresses is shown before you pay.` },
      { q: 'Are tolls included?', a: 'Yes. All bridge, tunnel and highway tolls are included in the price.' },
      { q: 'How long is the drive from Manhattan to Stamford?', a: 'About an hour without heavy traffic, and up to 90 minutes or more at weekday rush hour.' },
      { q: 'Can you take me from Stamford to an airport?', a: 'Yes, to JFK, LaGuardia, Newark or Westchester County Airport. Enter your Stamford address and the airport, add your flight number, and the price is shown before you book.' },
      { q: 'Can you set up a regular commute?', a: 'Yes. We can set up daily or weekly rides on a regular route, and companies can open a corporate account with several bookers and one monthly invoice. Call or email us to arrange it.' },
      { q: 'Can the car wait while I’m in a meeting?', a: 'Free waiting is 15 minutes after pickup. If you need the car to wait longer, an hourly booking includes waiting between stops.' },
    ],
    related: ['/nyc-to-greenwich-ct-car-service', '/nyc-to-new-haven-car-service', '/hourly-chauffeur-service', '/laguardia-car-service'],
  }),

  cityPage('new-haven-ct', {
    path: '/nyc-to-new-haven-car-service',
    city: 'New Haven, CT',
    linkText: 'NYC to New Haven, CT',
    eyebrow: 'Car service · NYC to New Haven, CT',
    title: <>NYC to New Haven, <em>Yale and beyond</em></>,
    lead: 'A private chauffeur from New York to Yale, Yale New Haven Hospital or anywhere in New Haven. Door to door, with room for luggage, boxes and the whole family.',
    heroImage: 'route-new-haven-yale',
    cta: 'Book your New Haven ride',
    introEyebrow: 'Black car service to New Haven',
    introTitle: <>Campus, hospital, <em>home</em></>,
    intro: [
      'New Haven is about 80 miles from Midtown Manhattan, straight up I-95 along the Connecticut shore, or on the Merritt Parkway for part of the way. Without heavy traffic the drive takes about an hour and forty minutes.',
      'Most of our New Haven rides are connected to Yale: students moving in and out with a car full of bags, parents visiting for a weekend or for Commencement in May, faculty and visitors heading to lectures and events. We also drive patients and families to appointments at Yale New Haven Hospital, where a door-to-door ride matters most.',
      'A private car saves the train to Union Station and the taxi across town at the other end. One price covers everyone in the vehicle, up to 3 passengers in a Sedan and 6 in an SUV.',
    ],
    sections: [
      {
        eyebrow: 'Timing and traffic',
        title: <>Planning <em>the drive</em></>,
        paragraphs: [
          'The slowest stretches are usually leaving the city through the Bronx, and I-95 around Stamford and Norwalk at rush hour. Move-in days and Commencement weekend at Yale bring heavy local traffic in New Haven itself, so allow extra time and book early.',
          'Your price is fixed once you book. If you need a stop on the way, add it when you book and the price updates before you pay.',
        ],
        image: 'night-ride-rear-seat',
        imageAlt: 'Rear seat of a private car on a drive out of the city',
      },
      {
        eyebrow: 'Where we go',
        title: <>Across <em>New Haven</em></>,
        paragraphs: ['Prices on this page are for central New Haven. Other addresses nearby are priced by distance and shown before you book.'],
        list: ['Yale University and downtown', 'Yale New Haven Hospital', 'Union Station', 'Tweed New Haven Airport (HVN)'],
      },
    ],
    faqTitle: 'NYC to New Haven questions',
    faq: [
      { q: 'How much is a car service from NYC to New Haven?', a: `From Midtown Manhattan to central New Haven, a Sedan is ${money(route('new-haven-ct').sedan)} and an SUV is ${money(route('new-haven-ct').suv)}, per vehicle. This is an estimate; the exact price for your addresses is shown before you pay.` },
      { q: 'Are tolls included?', a: 'Yes. All bridge, tunnel and highway tolls are included in the price.' },
      { q: 'How long is the drive to New Haven?', a: 'About 1 hour 40 minutes without heavy traffic. Weekday rush hours, Friday afternoons and Yale event weekends can add an hour or more.' },
      { q: 'Can you help with a Yale move-in?', a: 'Yes. Book an SUV for the most room: it takes up to 6 passengers and 6 large bags. Your chauffeur helps load and unload your bags.' },
      { q: 'Can you drive me to Yale New Haven Hospital?', a: 'Yes, to the main entrance or any address you give us. Add any instructions in the notes when you book.' },
      { q: 'Can I book the ride back to New York?', a: 'Yes. Book a second ride with your New Haven address as the pickup and your New York address as the drop-off.' },
    ],
    related: ['/nyc-to-stamford-ct-car-service', '/nyc-to-boston-car-service', '/nyc-to-greenwich-ct-car-service', '/point-to-point-car-service'],
  }),

  njSuburbsPage(),
];

export const CITY_PAGES = PAGES.filter(Boolean);
